import * as THREE from "three";
import { vol, blob, taperVol, surf, spike, scaleUV, bake, eyePair, richCreatures, type Piece } from "./creature.js";
import { monsterMaps, type MonsterSurface } from "./monsterTextures.js";
import { KRAKEN_URL, buildKrakenModel, disposeSkinned, preloadSeaModel, seaModelIfReady, seaModelsEnabled, updateKrakenModel, type KrakenRig } from "./seaModels.js";
import { deferSwap, holdLoadScreen, loadScreenOpen, registerWarmRoot, unregisterWarmRoot } from "./loadGate.js";
import { buildCreatureModel, creatureIfReady, creatureModelsEnabled, isCreatureKind, preloadCreature, updateCreatureModel, type CreatureKind, type CreatureRig } from "./creatureModels.js";

// hoisted per-frame scratch (3-4 fresh Vector3 per monster per frame added up)
const SCRATCH_POS = new THREE.Vector3();
const SCRATCH_MOUTH = new THREE.Vector3();
const SCRATCH_DIR = new THREE.Vector3();
const UP_AXIS = new THREE.Vector3(0, 1, 0);

// --- monster position dead-reckoning (fills genuine patch stalls) ---
// The mesh eases toward the newest replicated server position. On a healthy
// ~30 Hz link that follow is smooth, but when a patch gap actually stretches
// (WiFi power-save clump, a main-thread stall on a weak client) the target is
// frozen for several render frames, so the mesh eases to a STOP and then jumps
// when the next sample lands — the boss visibly steps. When a real stall is
// detected we extrapolate where the server box is DURING the gap from the
// sample-to-sample velocity. Bounds keep it provably safe:
//   - GRACE: never extrapolate the first 50 ms of a gap, so a link delivering
//     patches on time is BIT-IDENTICAL to the old follow (age never reaches it).
//   - LEAD:  extrapolate at most 150 ms of gap beyond the grace.
//   - MAXLEAD: hard distance cap at 0.7 m — below the loot goblin's 0.85 m hit
//     box half-width (server-side `goblinAabb`), so a stale velocity on a
//     stop/turn can never push the visible mesh outside its own hit box, which
//     is what a shooter aims at. The kaiju boxes are far larger; the cap is a
//     no-op for them.
//   - MAXSPD: clamp the velocity estimate (goblin tops 6.9 m/s; anything above
//     10 is a teleport/burrow and is handled by the snap below).
const DR_GRACE = 0.05;
const DR_LEAD = 0.15;
const DR_MAXLEAD = 0.7;
const DR_MAXSPD = 10;
const DR_TELE2 = 64; // m^2 — a jump this big is a burrow/respawn, snap + reset
// yaw dead-reckoning bounds — the facing analogue of the position DR above.
// Monster hit tests are server-side AABBs against m.x/y/z (goblinAabb/krakenAabb
// etc.), NOT facing, so a mesh's rendered yaw is purely cosmetic — extrapolating
// it has none of the "gun points the wrong way" hitreg risk that keeps remote
// PLAYERS on a raw-followed facing. So a turning boss can safely glide its
// heading through a stall. Same GRACE/LEAD window as the position DR (facing and
// body dead-reckon over the identical gap):
//   - MAXSPD: clamp the angular-velocity estimate (rad/s) so a coalesced short
//     interval can't read as a spin.
//   - MAXLEAD: hard cap on how far the facing may lead its last sample (rad), so
//     a stale turn-rate on a stop/reverse overshoots by at most this before the
//     follow eases it back — cosmetic, and bounded well under a half-turn.
const DR_YAW_MAXSPD = 8; // rad/s
const DR_YAW_MAXLEAD = 0.5; // rad (~29°)

/**
 * Giant map monsters (Skyhaven): low-poly kaiju with fully procedural
 * animation driven by the replicated {mode, modeT} + local time. No bones,
 * no clips — limbs are pivoted groups swung in code, so the whole show is
 * a handful of boxes per monster.
 */

/* The build toolkit (vol / taperVol / surf / spike / bake) moved to
 * `creature.ts` on 2026-08-01 (the BEAST pass) so the dragon and the sandworm
 * in worldEntities.ts could be rebuilt on the same primitives. */

export class MonsterMesh {
  readonly group = new THREE.Group();
  private legL!: THREE.Group;
  private legR!: THREE.Group;
  private armL!: THREE.Group;
  private armR!: THREE.Group;
  private tail: THREE.Group[] = [];
  private jaw: THREE.Object3D | null = null;
  /** Rich-tier KNEE pivots (shin + foot hang from them) — null on the lean
   *  tier, where each leg is still one rigid merged buffer. */
  private kneeL: THREE.Group | null = null;
  private kneeR: THREE.Group | null = null;
  /** Kong's jaw (rich tier only) — drops open in the chest-beat roar. */
  private kongJaw: THREE.Group | null = null;
  private torso!: THREE.Group;
  private breath: THREE.Mesh[] = [];
  private breathGlow: THREE.Mesh | null = null;
  private t = 0;

  private tentacles: { base: THREE.Group; mid: THREE.Group; tip: THREE.Group; phase: number }[] = [];
  /** The REALISTIC kraken (licensed glTF octopus, bones posed in code) —
   *  null on the procedural fallback (low-spec / TV / load failed or late). */
  private krakenRig: KrakenRig | null = null;
  /** The REALISTIC godzilla / kong / alien / goblin (original glTF assets from
   *  agent/gen-creatures.mjs, creatureModels.ts) — null on the procedural
   *  fallback (low-spec / TV / load failed or late). */
  private creatureRig: CreatureRig | null = null;
  /** registered as a loadGate warm root while this is a dormant spare */
  private warmReg = false;
  private disposed = false;

  // dead-reckoning state (see DR_* above): last replicated sample + its clock,
  // and the smoothed sample-to-sample velocity used to fill a genuine stall.
  private drInit = false;
  private drLiveX = 0;
  private drLiveY = 0;
  private drLiveZ = 0;
  private drLiveT = 0;
  private drVX = 0;
  private drVY = 0;
  private drVZ = 0;
  // yaw dead-reckoning (see DR_YAW_* below): last replicated facing + its
  // smoothed angular velocity, used to keep a turning boss GLIDING its facing
  // through a genuine stall instead of easing to a stop then snapping.
  private drYaw = 0;
  private drVYaw = 0;

  constructor(scene: THREE.Scene, readonly kind: string) {
    if (isCreatureKind(kind)) this.buildCreature(kind);
    else if (kind === "kraken") this.buildKraken();
    else if (kind === "penis") this.buildPenis();
    else this.buildKong();
    scene.add(this.group);
  }

  private buildProcedural(k: CreatureKind): void {
    if (k === "godzilla") this.buildGodzilla();
    else if (k === "alien") this.buildAlien();
    else if (k === "goblin") this.buildGoblin();
    else this.buildKong();
  }

  /** GODZILLA / KONG / ALIEN / GOBLIN: the realistic rigged glTF when it is
   *  parsed and allowed here, else the procedural build — and, exactly like
   *  the kraken, a dormant spare built under the load screen swaps the real
   *  model in when it lands inside the load-screen window (loadGate.ts). A
   *  late or failed load keeps the procedural model: never a mid-fight pop. */
  private buildCreature(k: CreatureKind): void {
    const ready = creatureIfReady(k);
    if (ready && this.mountCreatureModel(k, ready)) return;
    this.buildProcedural(k);
    if (ready || !creatureModelsEnabled()) return;
    const ticket = holdLoadScreen(preloadCreature(k).then((g) => {
      if (!g) return;
      const swap = (): void => {
        if (this.disposed || this.group.visible || this.creatureRig) return;
        for (const c of [...this.group.children]) { this.group.remove(c); disposeTree(c); }
        if (!this.mountCreatureModel(k, g)) this.buildProcedural(k);
      };
      // late: still a dormant spare at the next safe moment → real model
      if (loadScreenOpen(ticket)) swap(); else deferSwap(g.scene, swap);
    }));
  }

  /** Wears its realistic glTF (creature or kraken), not the procedural build. */
  get hasRealModel(): boolean { return this.creatureRig !== null || this.krakenRig !== null; }

  private mountCreatureModel(k: CreatureKind, g: Parameters<typeof buildCreatureModel>[1]): boolean {
    const rig = buildCreatureModel(k, g);
    if (!rig) return false;
    this.creatureRig = rig;
    this.group.add(rig.root);
    // a dormant spare is hidden; let precompile reveal it for the shader warm
    registerWarmRoot(this.group);
    this.warmReg = true;
    return true;
  }

  private sack: THREE.Group | null = null;

  /** THE LOOT GOBLIN — a knee-high neon bandit hauling a glowing sack of gold.
   *  Player-scale (the kaiju above are 10× this), proportions matched to the
   *  server's 1.7 × 1.9 m hit box; the emissive sack is the read-at-a-glance
   *  tell, the same job the kraken's golden eyes do. */
  private buildGoblin(): void {
    const skin = "#3fae5a", skinDk = "#2a7a3e", cloth = "#5c2d14", gold = "#ffd23f";
    const rich = richCreatures();
    this.torso = new THREE.Group();
    // BODY + HEAD, one merged warty-hide buffer: pot belly, hunched shoulders,
    // a wedge skull with a brow, a hooked snout and a jutting jaw. The old
    // goblin was five boxes; the read at 20 m is the hunch and the ears.
    const bodyPieces: Piece[] = [
      { g: vol(0.74, 0.5, 0.56, 0.95), p: [0, 0.86, 0.02] },      // pot belly
      { g: vol(0.66, 0.34, 0.44, 0.95), p: [0, 1.16, -0.02] },    // ribcage
      { g: vol(0.56, 0.16, 0.36, 1), p: [0, 1.3, -0.04], r: [0.18, 0, 0] }, // hunched shoulders
      { g: vol(0.2, 0.14, 0.2, 1), p: [0, 1.34, -0.06] },         // neck
      { g: vol(0.5, 0.42, 0.48, 0.9), p: [0, 1.46, -0.04] },      // cranium
      { g: vol(0.46, 0.07, 0.12, 1), p: [0, 1.53, -0.24] },       // brow ridge
      { g: taperVol(0.15, 0.3, 0.15, 0.25, 1), p: [0, 1.38, -0.32], r: [-1.25, 0, 0] }, // hooked snout
      { g: vol(0.3, 0.16, 0.26, 0.9), p: [0, 1.25, -0.2] },       // jaw
    ];
    if (!rich) {
      bodyPieces.push(
        { g: new THREE.ConeGeometry(0.1, 0.58, 7), p: [-0.44, 1.6, -0.02], r: [0, 0, 1.3] },
        { g: new THREE.ConeGeometry(0.1, 0.58, 7), p: [0.44, 1.6, -0.02], r: [0, 0, -1.3] },
      );
    } else {
      for (const sx of [-1, 1]) {
        bodyPieces.push(
          // EARS: flat, leaf-shaped, swept back — were two round cones
          { g: taperVol(0.26, 0.6, 0.06, 0.12, 1), p: [sx * 0.44, 1.6, 0], r: [0, -sx * 0.35, -sx * 1.3] },
          { g: blob(0.12, 0.08, 0.1, 1), p: [sx * 0.17, 1.4, -0.25] },                // cheekbone
          { g: new THREE.SphereGeometry(0.022, 6, 5), p: [sx * 0.05, 1.33, -0.42] },  // nose warts
          { g: blob(0.05, 0.1, 0.06, 1), p: [sx * 0.24, 1.18, -0.08] },               // collarbone knob
        );
      }
      bodyPieces.push(
        { g: new THREE.SphereGeometry(0.028, 6, 5), p: [0.01, 1.43, -0.38] },          // wart on the bridge
        { g: blob(0.26, 0.05, 0.08, 1), p: [0, 1.25, -0.33] },                          // pendulous lower lip
        { g: blob(0.32, 0.12, 0.08, 1), p: [0, 1.0, -0.27] },                           // belly fold
      );
    }
    const body = surf("hide", skin, 0.45, bodyPieces);
    // TEETH — a snaggle grin, six keratin pegs instead of the old white slab
    const teeth = surf("horn", "#fff8dc", 0.1, Array.from({ length: 6 }, (_, i) => ({
      g: spike(0.026, 0.09 + (i % 2) * 0.04),
      p: [-0.1 + i * 0.04, 1.29, -0.27] as const,
      r: [0, 0, (i % 2 ? 1 : -1) * Math.PI * 0.02 + (i < 3 ? 0 : Math.PI)] as const,
    })), { rough: 0.4, shadow: false });
    // RAG — real woven burlap wrapping the hips, with a shoulder strap
    const rag = surf("burlap", cloth, 0.35, [
      { g: vol(0.8, 0.28, 0.6, 0.95), p: [0, 0.6, 0] },
      { g: vol(0.16, 0.5, 0.1, 0.8), p: [-0.22, 0.95, -0.16], r: [0, 0, -0.35] },
    ], { rough: 0.95 });
    // big glowing coin-hungry eyes — a merged pair wearing the gold-iris eye
    // picture (iris glows, pupil stays black; were two flat gold balls)
    const eyes = eyePair("goblin", 0.072, [
      { p: [-0.14, 1.46, -0.24], yaw: 0.3 },
      { p: [0.14, 1.46, -0.24], yaw: -0.3 },
    ], 0.95, 0.15);
    // THE SACK — bulging burlap, golden, glowing; slung over the back
    this.sack = new THREE.Group();
    const bagGeo = new THREE.SphereGeometry(0.44, 14, 12);
    scaleUV(bagGeo, 3, 2);
    const bag = new THREE.Mesh(
      bagGeo,
      new THREE.MeshStandardMaterial(monsterMaps("burlap", {
        color: "#8a6a1e", roughness: 0.9, emissive: gold, emissiveIntensity: 0.35,
      })),
    );
    bag.scale.set(1, 1.2, 0.9); bag.castShadow = true;
    const knot = surf("burlap", cloth, 0.2, [
      { g: vol(0.14, 0.18, 0.14, 1), p: [0, 0.55, 0] },
      { g: new THREE.TorusGeometry(0.11, 0.03, 5, 10), p: [0, 0.45, 0], r: [Math.PI / 2, 0, 0] },
    ], { rough: 0.95, shadow: false });
    // coins spilling from the mouth of the sack — real flat discs at tumbling
    // angles, ONE merged buffer (were five separate gold beads)
    const coins = new THREE.Mesh(bake(Array.from({ length: 5 }, (_, i) => ({
      g: new THREE.CylinderGeometry(0.06, 0.06, 0.016, 12),
      p: [Math.sin(i * 2.1) * 0.2, 0.42 + (i % 2) * 0.12, Math.cos(i * 2.1) * 0.18] as const,
      r: [0.4 + i * 0.7, 0, i * 1.3] as const,
    }))), new THREE.MeshBasicMaterial({ color: gold }));
    this.sack.add(bag, knot, coins);
    this.sack.position.set(0, 1.15, 0.5);
    const mkArm = (x: number): THREE.Group => {
      const limb = surf("hide", skin, 0.4, [
        { g: taperVol(0.17, 0.24, 0.17, 0.8, 0.95), p: [0, -0.12, 0] },   // upper
        { g: new THREE.SphereGeometry(0.075, 8, 6), p: [0, -0.25, 0] },   // elbow
        { g: taperVol(0.13, 0.24, 0.13, 0.85, 0.95), p: [0, -0.38, 0] },  // forearm
        { g: vol(0.14, 0.13, 0.15, 0.95), p: [0, -0.52, -0.02] },         // hand
        ...(rich ? [0, 1, 2].map((i) => ({                                // knobbly fingers
          g: blob(0.042, 0.09, 0.05, 1), p: [(i - 1) * 0.045, -0.59, -0.05] as const, r: [-0.4, 0, 0] as const,
        })) : []),
      ], { shadow: false });
      const claws = surf("horn", skinDk, 0.12, [0, 1, 2].map((i) => ({
        g: spike(0.02, 0.11),
        p: [(i - 1) * 0.045, -0.6, -0.06] as const,
        r: [-2.4, 0, 0] as const,
      })), { rough: 0.45, shadow: false });
      return this.pivot(x, 1.1, 0, limb, claws);
    };
    this.armL = mkArm(-0.44);
    this.armR = mkArm(0.44);
    const mkLeg = (x: number): THREE.Group => {
      const limb = surf("hide", skin, 0.4, [
        { g: taperVol(0.21, 0.26, 0.22, 0.75, 0.95), p: [0, -0.13, 0] },  // thigh
        { g: taperVol(0.15, 0.24, 0.15, 0.9, 0.95), p: [0, -0.36, 0.03] },// shin
        { g: vol(0.2, 0.1, 0.34, 0.9), p: [0, -0.46, -0.06] },            // foot
        ...(rich ? [
          { g: new THREE.SphereGeometry(0.06, 8, 6), p: [0, -0.25, -0.03] as const },  // knobbly knee
          ...[0, 1, 2].map((i) => ({                                                    // long toes
            g: blob(0.055, 0.065, 0.11, 1), p: [(i - 1) * 0.06, -0.47, -0.2] as const,
          })),
        ] : []),
      ], { shadow: false });
      const claws = surf("horn", skinDk, 0.12, [0, 1, 2].map((i) => ({
        g: spike(0.022, 0.1),
        p: [(i - 1) * 0.06, -0.47, -0.23] as const,
        r: [-1.9, 0, 0] as const,
      })), { rough: 0.45, shadow: false });
      return this.pivot(x, 0.52, 0, limb, claws);
    };
    this.legL = mkLeg(-0.2);
    this.legR = mkLeg(0.2);
    this.torso.add(body, teeth, rag, eyes, this.sack, this.armL, this.armR);
    this.group.add(this.torso, this.legL, this.legR);
  }

  /** THE KRAKEN. Full-quality clients get the realistic glTF octopus
   *  (seaModels.ts) — same footprint, same modes, arms posed in code. If the
   *  asset is not parsed yet this builds the procedural squid and, while this
   *  mesh is still a DORMANT spare under the load screen (prebuildMapBeasts
   *  hides it; precompile() waits on the gate before compiling), swaps the
   *  real model in. A late or failed load keeps the squid — never a mid-fight
   *  pop. */
  private buildKraken(): void {
    const ready = seaModelIfReady(KRAKEN_URL);
    if (ready && this.mountKrakenModel(ready)) return;
    this.buildKrakenProcedural();
    if (ready || !seaModelsEnabled()) return;
    const ticket = holdLoadScreen(preloadSeaModel(KRAKEN_URL).then((g) => {
      if (!g) return;
      const swap = (): void => {
        if (this.disposed || this.group.visible || this.krakenRig) return;
        // drop the squid (its own buffers/materials; shared textures survive)
        for (const c of [...this.group.children]) { this.group.remove(c); disposeTree(c); }
        this.tentacles = [];
        if (!this.mountKrakenModel(g)) this.buildKrakenProcedural();
      };
      if (loadScreenOpen(ticket)) swap(); else deferSwap(g.scene, swap);
    }));
  }

  private mountKrakenModel(g: Parameters<typeof buildKrakenModel>[0]): boolean {
    const rig = buildKrakenModel(g);
    if (!rig) return false;
    this.krakenRig = rig;
    this.group.add(rig.root);
    registerWarmRoot(this.group); // dormant spare: precompile reveals it (loadGate warm roots)
    this.warmReg = true;
    return true;
  }

  /** THE KRAKEN (procedural fallback) — an elongated violet squid: a tapering mantle with head-fins
   *  and bioluminescent spots, two big golden glowing eyes with slit pupils, a
   *  beak, and EIGHT three-segment tentacles (with pale suckers) that writhe,
   *  rear up (windup) and SLAM (mode 2). mode 9 = slain → limp + sink. */
  private buildKrakenProcedural(): void {
    const skin = "#3a2358", skinLt = "#5a3a86", belly = "#7a4aa8", glow = "#c86bff", suck = "#e7c8ff";
    const rich = richCreatures();
    // MANTLE — a rounded body tapering up to a pointed crown (squid head), now
    // in wet papillose cephalopod skin with a soft keel down each flank.
    const mantleGeo = new THREE.SphereGeometry(5.4, 24, 20);
    scaleUV(mantleGeo, 8, 5);
    const mantle = new THREE.Mesh(mantleGeo, new THREE.MeshStandardMaterial(
      monsterMaps("ceph", { color: skin, roughness: 0.55, metalness: 0.05 })));
    mantle.scale.set(1, 1.5, 1.05); mantle.position.y = 4.2; mantle.castShadow = true; this.group.add(mantle);
    const keelPieces: Piece[] = [ // mantle keels — the flank ridges
      { g: taperVol(0.9, 7.0, 1.6, 0.2, 1), p: [-4.3, 5.0, 0.6], r: [0, 0, 0.16] },
      { g: taperVol(0.9, 7.0, 1.6, 0.2, 1), p: [4.3, 5.0, 0.6], r: [0, 0, -0.16] },
    ];
    if (rich) {
      // rich tier: fleshy LIDS ringing each eye, and the muscular collar where
      // the arms leave the mantle
      for (const sx of [-1, 1]) {
        keelPieces.push({ g: new THREE.TorusGeometry(1.22, 0.3, 8, 22), p: [sx * 2.72, 4.7, -4.82], r: [0, -sx * 0.25, 0], s: [1, 1.05, 0.7] });
      }
      keelPieces.push({ g: new THREE.TorusGeometry(4.55, 0.55, 8, 32), p: [0, 1.35, 0], r: [Math.PI / 2, 0, 0] });
    }
    this.group.add(surf("ceph", skinLt, 2.2, keelPieces, { rough: 0.5 }));
    const crownGeo = new THREE.ConeGeometry(3.4, 5.2, 20);
    scaleUV(crownGeo, 6, 3);
    const crown = new THREE.Mesh(crownGeo, new THREE.MeshStandardMaterial(
      monsterMaps("ceph", { color: skinLt, roughness: 0.55, metalness: 0.05 })));
    crown.position.y = 9.6; this.group.add(crown);
    // HEAD FINS — both in ONE buffer now (were a mesh + material each)
    const finGeo = bake([-1, 1].map((sx) => ({
      g: new THREE.ConeGeometry(1.5, 4.2, 7), p: [sx * 4.7, 6.8, 0.4] as const, r: [0, 0, sx * 1.15] as const,
    })));
    scaleUV(finGeo, 3, 2);
    this.group.add(new THREE.Mesh(finGeo, new THREE.MeshStandardMaterial(
      monsterMaps("ceph", { color: skinLt, roughness: 0.5, emissive: glow, emissiveIntensity: 0.18 }))));
    // bioluminescent spots ringing the mantle — ONE merged buffer (were 16
    // separate sphere meshes, a draw call each)
    const spots = new THREE.Mesh(bake(Array.from({ length: 16 }, (_, i) => {
      const a = (i / 16) * Math.PI * 2;
      return { g: new THREE.SphereGeometry(0.26, 8, 8), p: [Math.cos(a) * 4.3, 3.2 + (i % 3) * 1.7, Math.sin(a) * 4.5 - 0.4] as const };
    })), new THREE.MeshBasicMaterial({ color: glow }));
    this.group.add(spots);
    // EYES — golden glowing iris with the black slit pupil painted into it, on
    // one merged pair (was sclera + iris + pupil box + highlight, x2 = 8 meshes);
    // the ball is stretched forward so its front sits exactly where the old
    // iris did
    this.group.add(eyePair("squid", 1.55, [
      { p: [-2.7, 4.7, -3.75], yaw: 0.25, s: [1, 1, 1.15] },
      { p: [2.7, 4.7, -3.75], yaw: -0.25, s: [1, 1, 1.15] },
    ], 1.15, 0.1));
    // BEAK — a parrot beak: two hooked keratin mandibles, not one 4-sided cone
    this.group.add(surf("horn", "#241436", 1.2, [
      { g: spike(1.15, 2.2), p: [0, 2.1, -4.7], r: [-Math.PI / 2 - 0.5, 0, 0], s: [1, 1, 0.7] },
      { g: spike(0.95, 1.7), p: [0, 1.2, -4.6], r: [-Math.PI / 2 + 0.55, 0, 0], s: [1, 1, 0.7] },
    ], { rough: 0.35, metal: 0.1 }));
    // TENTACLES — 8, three tapering segments each; two long feeding arms.
    // Each segment is ONE merged buffer: the arm plus a DOUBLE row of suckers
    // (the old build spent a draw call per sucker — 72 of them across the
    // animal, on 0.13 m spheres nobody could resolve).
    // 2026-10-23: the radii now taper CONTINUOUSLY from the mantle to a point
    // (they used to zig-zag 0.98→0.6 | 0.55→0.88 | 0.26→0.58, so every arm
    // ended in a blunt 0.58 m stump); the two feeding arms end in a flattened
    // sucker CLUB on the rich tier, like the real animal's.
    const N = 8;
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      const long = i % 4 === 0;
      const seg = (len: number, r0: number, r1: number, col: string, club = false): THREE.Group => {
        const g = new THREE.Group();
        const arm: Piece[] = [{ g: new THREE.CylinderGeometry(r1, r0, len, 14, 1), p: [0, len / 2, 0] }];
        const cups: Piece[] = [];
        for (let s = 0; s < 6; s++) {
          const y = (len / 6) * (s + 0.5);
          const rr = r0 + ((r1 - r0) * y) / len;      // the arm's radius at this height
          for (const sx of [-0.45, 0.45]) {
            // small and shallow: a sucker is a rim on the arm, not a bead glued
            // to it — the first pass made them 0.26 m spheres and the animal
            // read as polka dots from 25 m.
            cups.push({
              g: new THREE.SphereGeometry(0.05 + rr * 0.075, 7, 5),
              p: [sx * rr * 0.95, y, -rr * 0.92] as const,
              s: [1, 1, 0.45] as const,
            });
          }
        }
        if (club) {
          arm.push({ g: blob(0.62, 1.5, 0.44, 1), p: [0, len - 0.55, 0.02] });
          for (let s = 0; s < 4; s++) {
            for (const sx of [-0.16, 0.16]) {
              cups.push({ g: new THREE.SphereGeometry(0.075, 7, 5), p: [sx, len - 1.1 + s * 0.32, -0.21] as const, s: [1, 1, 0.45] as const });
            }
          }
        }
        g.add(
          surf("ceph", col, 1.0, arm, { rough: 0.45, shadow: len > 4 }),
          surf("ceph", suck, 0.35, cups, { rough: 0.35, shadow: false }),
        );
        return g;
      };
      const tip = this.pivot(0, 4.2, 0, seg(long ? 5 : 3.6, 0.38, long ? 0.2 : 0.09, belly, long && rich));
      const mid = this.pivot(0, 4.4, 0, seg(4.5, 0.64, 0.4, skinLt), tip);
      const base = this.pivot(Math.cos(a) * 4.4, 1.6, Math.sin(a) * 4.4, seg(4.4, 0.98, 0.66, skin), mid);
      base.rotation.y = a; base.rotation.x = -0.55;
      this.group.add(base);
      this.tentacles.push({ base, mid, tip, phase: i * 0.8 });
    }
  }

  private pivot(x: number, y: number, z: number, ...parts: THREE.Object3D[]): THREE.Group {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    g.add(...parts);
    return g;
  }

  private buildGodzilla(): void {
    const skin = "#2c3b2f", belly = "#5a6b52", fin = "#7df0ff", claw = "#d8d2c0", gum = "#5a1c1e";
    const rich = richCreatures();
    this.torso = new THREE.Group();
    // TORSO — a stack of elliptical volumes (hips → barrel chest → shoulders →
    // neck) instead of one 4.6 × 6.5 × 3.6 slab, so the animal has a waist, a
    // ribcage and a throat in profile. Scaled hide over the lot.
    const bodyPieces: Piece[] = [
      { g: vol(4.1, 2.6, 3.4, 0.9), p: [0, 5.4, 0.25] },              // hips
      { g: vol(4.7, 3.4, 3.8, 0.85), p: [0, 8.0, -0.1] },             // ribcage
      { g: vol(4.2, 1.9, 3.2, 0.9), p: [0, 10.0, -0.35], r: [0.12, 0, 0] }, // shoulders
      { g: taperVol(2.1, 1.9, 2.2, 0.85, 0.95), p: [0, 10.9, -0.75], r: [-0.5, 0, 0] }, // neck
      { g: vol(2.5, 1.7, 2.4, 0.9), p: [0, 11.85, -1.3] },            // cranium
      { g: taperVol(1.9, 1.9, 2.0, 0.72, 0.9), p: [0, 11.4, -2.6], r: [-1.35, 0, 0] }, // snout
      { g: vol(2.5, 0.34, 0.9, 1), p: [-0.02, 12.35, -2.1], r: [0.2, 0, 0] }, // brow ridge
      { g: vol(0.5, 0.5, 0.5, 1), p: [-0.9, 12.5, -1.9] },            // temple horns
      { g: vol(0.5, 0.5, 0.5, 1), p: [0.9, 12.5, -1.9] },
    ];
    if (rich) {
      // REALISTIC ANATOMY (rich tier): the muscle masses and soft-tissue folds a
      // real big reptile shows in profile — pectorals over the plastron, jaw
      // muscles behind the eye, a folded dewlap under the throat, raised nasal
      // bosses on the snout and a scaly lid ring around each eye.
      for (const sx of [-1, 1]) {
        bodyPieces.push(
          { g: blob(1.9, 1.3, 1.0, 0.95), p: [sx * 1.3, 9.3, -1.55], r: [0.15, 0, -sx * 0.12] },  // pectoral
          { g: blob(0.75, 0.85, 1.05, 1), p: [sx * 1.05, 11.45, -1.35] },                        // jaw muscle
          { g: blob(0.42, 0.3, 0.5, 1), p: [sx * 0.33, 12.12, -3.38], r: [0.3, 0, 0] },          // nasal boss
          { g: new THREE.TorusGeometry(0.27, 0.075, 6, 14), p: [sx * 0.86, 12.05, -2.47], r: [0, sx * 0.45, 0] }, // lid ring
          { g: blob(1.25, 1.6, 1.1, 0.9), p: [sx * 1.15, 6.6, -0.2], r: [0, 0, sx * 0.1] },     // flank / oblique
        );
      }
      for (let i = 0; i < 3; i++) {  // throat folds (dewlap)
        bodyPieces.push({ g: blob(1.8 - i * 0.22, 0.24, 1.25, 1), p: [0, 10.15 + i * 0.32, -1.45 - i * 0.28], r: [-0.5, 0, 0] });
      }
    }
    const body = surf("scale", skin, 1.9, bodyPieces);
    // BELLY PLATES — banded plastron down the chest, its own chitin-hard band
    const chest = surf("chitin", belly, 1.1, Array.from({ length: 6 }, (_, i) => ({
      g: vol(3.0 - Math.abs(i - 2.5) * 0.26, 0.62, 0.5, 1),
      p: [0, 5.5 + i * 0.85, -1.75 - (i > 3 ? (i - 3) * 0.12 : 0)] as const,
    })), { rough: 0.6 });
    const jaw = surf("scale", skin, 1.5, [
      { g: vol(1.9, 0.62, 2.3, 0.85), p: [0, -0.32, -0.95] },
      { g: vol(0.9, 0.5, 0.7, 1), p: [0, -0.35, -2.0] },              // chin
      ...(rich ? [                                                     // jowls
        { g: blob(0.55, 0.5, 1.4, 1), p: [-0.85, -0.2, -0.55] as const },
        { g: blob(0.55, 0.5, 1.4, 1), p: [0.85, -0.2, -0.55] as const },
      ] : []),
    ]);
    this.jaw = this.pivot(0, 10.75, -1.05, jaw);
    if (rich) {
      // MOUTH INTERIOR — a wet palate under the snout and a tongue on the jaw
      // floor, plus the two dark nostril pits, so the open-jaw tell shows a
      // MOUTH and not the inside of a hide shell.
      this.torso.add(surf("flesh", gum, 0.5, [
        { g: blob(1.35, 0.14, 1.9, 1), p: [0, 10.5, -2.6], r: [0.36, 0, 0] },
        { g: new THREE.SphereGeometry(0.1, 8, 6), p: [-0.33, 12.17, -3.62], s: [1, 0.6, 1] },
        { g: new THREE.SphereGeometry(0.1, 8, 6), p: [0.33, 12.17, -3.62], s: [1, 0.6, 1] },
      ], { rough: 0.35, shadow: false }));
      this.jaw.add(surf("flesh", gum, 0.5, [
        { g: blob(0.95, 0.22, 1.7, 1), p: [0, -0.03, -1.15] },
      ], { rough: 0.3, shadow: false }));
    }
    // TEETH — two rows of keratin, upper fixed to the skull, lower on the jaw
    const upperTeeth = surf("horn", claw, 0.6, Array.from({ length: 12 }, (_, i) => {
      const s = i < 6 ? -1 : 1, k = i % 6;
      return {
        g: spike(0.15 - k * 0.012, 0.62 - k * 0.04),
        p: [s * (0.32 + k * 0.16), 10.95 + k * 0.06, -3.35 + k * 0.42] as const,
        r: [0, 0, Math.PI] as const,
      };
    }), { rough: 0.4, shadow: false });
    const lowerTeeth = surf("horn", claw, 0.6, Array.from({ length: 10 }, (_, i) => {
      const s = i < 5 ? -1 : 1, k = i % 5;
      return {
        g: spike(0.13 - k * 0.012, 0.5 - k * 0.03),
        p: [s * (0.3 + k * 0.17), -0.05, -2.3 + k * 0.42] as const,
      };
    }), { rough: 0.4, shadow: false });
    this.jaw.add(lowerTeeth);
    // EYES — one merged pair wearing the amber slit-pupil reptile picture; the
    // iris is its own emissive map, so the glow tell survives with a black
    // pupil in it (was two plain amber balls, glowing pupil and all)
    const eyes = eyePair("reptile", 0.25, [
      { p: [-0.84, 12.05, -2.45], yaw: 0.45, s: [1, 1.12, 1] },
      { p: [0.84, 12.05, -2.45], yaw: -0.45, s: [1, 1.12, 1] },
    ], 1.1, 0.15);
    // GLOWING DORSAL FINS — three staggered rows of maple-leaf plates up the
    // spine and onto the tail (was a single row of 5 four-sided cones), merged
    // into ONE emissive buffer so the extra 13 plates cost no extra draw call.
    const finPieces: Piece[] = [];
    for (let i = 0; i < 9; i++) {
      const t = i / 8;
      const y = 11.2 - i * 1.05, z = 1.55 + t * 0.5;
      finPieces.push({ g: spike(1.0 - t * 0.55, 2.1 - t * 1.0), p: [0, y, z], r: [0.42, 0, 0] });
      if (i > 0 && i < 7) {
        for (const sx of [-1, 1]) {
          finPieces.push({
            g: spike(0.5 - t * 0.24, 1.1 - t * 0.5),
            p: [sx * (0.62 + t * 0.1), y - 0.35, z - 0.25] as const,
            r: [0.42, 0, sx * 0.5] as const,
          });
        }
      }
    }
    const fins = surf("horn", "#2a4438", 1.0, finPieces, { emissive: fin, ei: 0.85, rough: 0.35 });
    // ARMS — small and classic, but now upper/fore/hand with three claws
    const mkArm = (x: number): THREE.Group => {
      const limb = surf("scale", skin, 1.3, [
        { g: taperVol(1.05, 1.5, 1.05, 0.82, 0.9), p: [0, -0.75, 0] },
        { g: new THREE.SphereGeometry(0.42, 10, 8), p: [0, -1.5, 0] },
        { g: taperVol(0.85, 1.4, 0.85, 0.88, 0.9), p: [0, -2.2, -0.15], r: [0.25, 0, 0] },
        { g: vol(0.8, 0.55, 0.9, 0.95), p: [0, -2.95, -0.35] },
        ...(rich ? [
          { g: blob(0.75, 1.0, 0.6, 1), p: [0, -0.75, -0.42] as const },            // biceps
          ...[-1, 0, 1].map((i) => ({                                                // digits
            g: taperVol(0.22, 0.5, 0.24, 0.75, 1), p: [i * 0.24, -3.1, -0.62] as const, r: [-2.0, 0, 0] as const,
          })),
        ] : []),
      ]);
      const claws = surf("horn", claw, 0.5, [-1, 0, 1].map((i) => ({
        g: spike(0.11, 0.55),
        p: [i * 0.24, -3.15, -0.7] as const,
        r: [-2.2, 0, 0] as const,
      })), { rough: 0.4, shadow: false });
      return this.pivot(x, 9.6, -0.5, limb, claws);
    };
    this.armL = mkArm(-2.4);
    this.armR = mkArm(2.4);
    this.torso.add(body, chest, this.jaw, upperTeeth, eyes, fins, this.armL, this.armR);
    // LEGS — digitigrade thigh/shin/ankle with a three-toed clawed foot. On the
    // rich tier the shin + foot hang from their own KNEE pivot so the walk can
    // flex the knee through the swing (a stiff-legged stilt-walk was the
    // single least animal thing about the gait).
    const mkLeg = (x: number): [THREE.Group, THREE.Group | null] => {
      const KY = -2.7, KZ = 0.1;                                    // knee, in hip space
      const upper: Piece[] = [
        { g: taperVol(1.9, 2.7, 2.2, 0.72, 0.9), p: [0, -1.35, 0] },        // thigh
        { g: new THREE.SphereGeometry(0.72, 12, 8), p: [0, KY, KZ] },       // knee
      ];
      // lower-leg pieces authored in HIP space, shifted into knee space below
      const lower: [THREE.BufferGeometry, number, number, number, number][] = [
        [taperVol(1.35, 2.0, 1.5, 0.85, 0.9), 0, -3.6, 0.15, 0],            // shin
        [vol(1.6, 0.7, 2.4, 0.9), 0, -4.75, -0.45, 0],                      // foot
      ];
      if (rich) {
        upper.push({ g: blob(1.35, 1.8, 0.8, 1), p: [0, -1.25, -0.95] });   // quadriceps
        lower.push([blob(0.95, 1.3, 0.75, 1), 0, -3.35, 0.72, 0]);          // calf
        for (const i of [-1, 0, 1]) lower.push([blob(0.44, 0.42, 0.85, 1), i * 0.5, -4.85, -1.45, 0]); // toes
      }
      const clawP = [-1, 0, 1].map((i) => [spike(0.17, 0.72), i * 0.5, -4.9, -1.7, -1.75] as const);
      const oy = rich ? KY : 0, oz = rich ? KZ : 0;
      const lowerPieces: Piece[] = lower.map(([g, px, py, pz, rx]) => ({ g, p: [px, py - oy, pz - oz] as const, r: [rx, 0, 0] as const }));
      const clawPieces: Piece[] = clawP.map(([g, px, py, pz, rx]) => ({ g, p: [px, py - oy, pz - oz] as const, r: [rx, 0, 0] as const }));
      const claws = surf("horn", claw, 0.6, clawPieces, { rough: 0.4, shadow: false });
      if (!rich) return [this.pivot(x, 4.6, 0, surf("scale", skin, 1.6, [...upper, ...lowerPieces]), claws), null];
      const knee = this.pivot(0, KY, KZ, surf("scale", skin, 1.6, lowerPieces), claws);
      return [this.pivot(x, 4.6, 0, surf("scale", skin, 1.6, upper), knee), knee];
    };
    [this.legL, this.kneeL] = mkLeg(-1.5);
    [this.legR, this.kneeR] = mkLeg(1.5);
    // TAIL: 5 swinging tapered segments (was 3 boxes that ended in a stump).
    // Rich tier: a row of backward-raked osteoderm spikes down the top of each
    // segment, merged into the segment's own buffer (no extra draw call).
    let px = 0, py = 5.4, pz = 2.0;
    let parent: THREE.Object3D = this.group;
    for (let i = 0; i < 5; i++) {
      const w = 1.8 - i * 0.3, len = 3.0 - i * 0.25;
      const segPieces: Piece[] = [
        { g: taperVol(w, len, w * 0.95, 0.78, 0.9), p: [0, 0, len / 2], r: [Math.PI / 2, 0, 0] },
      ];
      if (rich) {
        for (const f of [0.3, 0.72]) {
          const hh = (w * 0.95 / 2) * (1 - 0.22 * f);
          segPieces.push({ g: spike(0.16 * w, 0.42 * w), p: [0, hh * 0.8 + 0.1 * w, len * f], r: [0.7, Math.PI, 0] });
        }
      }
      const seg = surf("scale", skin, 1.4, segPieces);
      const piv = this.pivot(px, py, pz, seg);
      parent.add(piv);
      this.tail.push(piv);
      parent = piv;
      px = 0; py = 0; pz = len;
    }
    // (fire-breath VISUAL removed — it read as random noise; the jaw still
    // opens as the tell and the server still deals the fire damage)
    this.group.add(this.torso, this.legL, this.legR);
  }

  private buildKong(): void {
    const fur = "#3a2c22", face = "#8a7364", nail = "#2b2119";
    const rich = richCreatures();
    this.torso = new THREE.Group();
    // TORSO — the ape read is a huge trapezius wedge over a barrel chest and a
    // narrow gut. All in matted fur, which is what a 4 m silverback flank needs
    // to look like anything other than a brown crate.
    const bodyPieces: Piece[] = [
      { g: vol(3.4, 2.2, 2.6, 0.9), p: [0, 4.2, 0.1] },                 // gut
      { g: vol(4.5, 2.6, 3.1, 0.85), p: [0, 6.0, -0.05] },              // barrel chest
      { g: vol(5.4, 1.5, 2.7, 0.9), p: [0, 7.5, 0.05], r: [0.12, 0, 0] },// shoulder mass
      { g: vol(2.4, 1.0, 2.0, 1), p: [0, 8.0, -0.4] },                  // thick neck
      { g: vol(2.2, 1.9, 2.1, 0.9), p: [0, 8.9, -0.65] },               // cranium
      { g: vol(0.5, 0.9, 0.5, 1), p: [0, 9.85, -0.6] },                 // sagittal crest
      { g: vol(0.5, 0.55, 0.18, 1), p: [-1.15, 8.9, -0.5] },            // ears
      { g: vol(0.5, 0.55, 0.18, 1), p: [1.15, 8.9, -0.5] },
    ];
    if (rich) {
      // SHAG (rich tier): hanging clumps of long guard hair off the shoulders
      // and the back of the neck — the ragged edge is what separates a pelt
      // from a fur-textured volume at silhouette distance.
      for (const sx of [-1, 1]) {
        for (const [z, len] of [[-0.7, 1.0], [0.15, 1.2], [1.0, 1.0]] as const) {
          bodyPieces.push({ g: taperVol(0.6, len, 0.4, 0.2, 1), p: [sx * 2.45, 6.85, z], r: [Math.PI, 0, -sx * 0.28] });
        }
      }
      for (const sx of [-0.6, 0, 0.6]) {
        bodyPieces.push({ g: taperVol(0.6, 0.9, 0.4, 0.2, 1), p: [sx, 7.85, 1.15], r: [Math.PI - 0.5, 0, 0] });
      }
      bodyPieces.push({ g: vol(2.6, 1.6, 1.4, 0.9), p: [0, 7.3, 0.9] });  // upper-back hump
    }
    const body = surf("fur", fur, 1.7, bodyPieces);
    // FACE + CHEST — bare skin, the contrast that reads as "ape" at range
    const barePieces: Piece[] = [
      // chest: pectorals over a narrowing belly, not one pale slab
      { g: vol(2.3, 1.1, 0.6, 1), p: [0, 6.35, -1.28] },
      { g: taperVol(1.9, 1.5, 0.52, 0.62, 1), p: [0, 5.2, -1.2] },
      { g: vol(1.5, 1.15, 0.9, 0.9), p: [0, 8.45, -1.85] },             // muzzle
      { g: vol(1.7, 0.42, 0.5, 1), p: [0, 9.3, -1.6], r: [0.15, 0, 0] },// brow shelf
    ];
    if (!rich) barePieces.push({ g: vol(1.2, 0.3, 0.35, 1), p: [0, 8.0, -1.7] }); // jaw
    else {
      // a real gorilla face: a flat nasal ridge flaring into big nostrils, a
      // heavy upper lip, cheek pads, and a split pectoral
      barePieces.push(
        { g: blob(0.5, 0.55, 0.3, 1), p: [0, 8.82, -2.18] },              // nasal ridge
        { g: blob(0.34, 0.3, 0.3, 1), p: [-0.27, 8.52, -2.2] },           // nostril flares
        { g: blob(0.34, 0.3, 0.3, 1), p: [0.27, 8.52, -2.2] },
        { g: blob(1.35, 0.3, 0.45, 1), p: [0, 8.12, -2.08] },             // upper lip
        { g: blob(0.55, 0.75, 0.45, 1), p: [-0.66, 8.55, -1.85] },        // cheek pads
        { g: blob(0.55, 0.75, 0.45, 1), p: [0.66, 8.55, -1.85] },
        { g: blob(1.2, 0.9, 0.5, 1), p: [-0.62, 6.4, -1.36] },            // pectorals
        { g: blob(1.2, 0.9, 0.5, 1), p: [0.62, 6.4, -1.36] },
      );
    }
    const bare = surf("flesh", face, 1.0, barePieces, { rough: 0.7 });
    // EYES — deep-set under the brow: a brown iris in the dark sclera a great
    // ape actually has (were two black beads)
    const eyes = eyePair("ape", 0.18, [
      { p: [-0.52, 9.05, -1.72], yaw: 0.2 },
      { p: [0.52, 9.05, -1.72], yaw: -0.2 },
    ], 0, 0.1);
    // NOSTRILS (+ the mouth cavity on the rich tier) — one dark, unmapped,
    // merged buffer (was two separate nostril meshes)
    const darkMat = new THREE.MeshStandardMaterial({ color: "#120d08", roughness: 0.55 });
    darkMat.userData.bare = true;
    const darkPieces: Piece[] = rich
      ? [
        { g: new THREE.SphereGeometry(0.12, 8, 6), p: [-0.26, 8.48, -2.32], s: [1, 0.7, 0.6] },
        { g: new THREE.SphereGeometry(0.12, 8, 6), p: [0.26, 8.48, -2.32], s: [1, 0.7, 0.6] },
        { g: new THREE.SphereGeometry(0.4, 10, 8), p: [0, 7.95, -1.9], s: [1.4, 0.5, 1] },
      ]
      : [
        { g: new THREE.SphereGeometry(0.13, 8, 6), p: [-0.24, 8.5, -2.28] },
        { g: new THREE.SphereGeometry(0.13, 8, 6), p: [0.24, 8.5, -2.28] },
      ];
    const dark = new THREE.Mesh(bake(darkPieces), darkMat);
    const faceParts: THREE.Object3D[] = [bare, eyes, dark];
    if (rich) {
      // JAW on its own pivot so the chest-beat ROARS: lower lip + chin, with
      // the lower canines; the upper canines hang from the skull
      const jawMesh = surf("flesh", face, 0.8, [
        { g: blob(1.25, 0.42, 0.8, 1), p: [0, -0.25, -0.45] },
        { g: blob(0.8, 0.3, 0.3, 1), p: [0, -0.4, -0.7] },                // chin
        { g: blob(1.1, 0.2, 0.3, 1), p: [0, -0.08, -0.8] },               // lower lip
      ], { rough: 0.7, shadow: false });
      const lowerFangs = surf("horn", "#e8dcc0", 0.3, [-1, 1].map((sx) => ({
        g: spike(0.06, 0.24), p: [sx * 0.36, -0.02, -0.68] as const,
      })), { rough: 0.35, shadow: false });
      this.kongJaw = this.pivot(0, 8.0, -1.4, jawMesh, lowerFangs);
      const upperFangs = surf("horn", "#e8dcc0", 0.3, [-1, 1].map((sx) => ({
        g: spike(0.07, 0.3), p: [sx * 0.38, 8.05, -2.12] as const, r: [0, 0, Math.PI] as const,
      })), { rough: 0.35, shadow: false });
      faceParts.push(this.kongJaw, upperFangs);
    }
    // huge arms — his signature: upper, forearm, and a knuckled fist
    const mkArm = (x: number): THREE.Group => {
      const inner = x < 0 ? 1 : -1;                                       // toward the body midline
      const limb = surf("fur", fur, 1.5, [
        { g: taperVol(1.75, 3.2, 1.7, 0.78, 0.9), p: [0, -1.6, 0] },     // upper
        { g: new THREE.SphereGeometry(0.72, 12, 8), p: [0, -3.25, 0] },  // elbow
        { g: taperVol(1.35, 2.9, 1.35, 0.9, 0.9), p: [0, -4.7, 0] },     // forearm
        ...(rich ? [
          { g: taperVol(0.55, 1.0, 0.4, 0.2, 1), p: [0, -3.5, 0.6] as const, r: [2.1, 0, 0] as const },   // elbow tuft
          { g: taperVol(0.5, 0.8, 0.35, 0.2, 1), p: [-inner * 0.5, -4.6, 0.5] as const, r: [2.3, 0, -inner * 0.3] as const },
          { g: blob(1.2, 1.5, 1.0, 1), p: [0, -1.4, -0.55] as const },                                    // biceps
        ] : []),
      ]);
      const knuckX = rich ? [-0.55, -0.18, 0.18, 0.55] : [-0.5, 0, 0.5];
      const hand = surf("flesh", face, 0.8, [
        { g: vol(1.55, 1.1, 1.6, 0.9), p: [0, -6.35, 0] },               // fist
        ...knuckX.map((kx) => ({                                          // knuckles
          g: new THREE.SphereGeometry(rich ? 0.3 : 0.36, 10, 8), p: [kx, -6.65, -0.62] as const,
        })),
        ...(rich ? [
          // curled finger segments under the knuckles, and an opposed thumb
          ...knuckX.map((kx) => ({ g: blob(0.33, 0.42, 0.45, 1), p: [kx, -6.95, -0.42] as const })),
          { g: taperVol(0.42, 0.85, 0.42, 0.7, 1), p: [inner * 0.75, -6.15, -0.55] as const, r: [-0.5, 0, inner * 0.6] as const },
        ] : []),
      ], { rough: 0.7 });
      const nails = surf("horn", nail, 0.5, knuckX.map((kx) => ({
        g: spike(rich ? 0.11 : 0.13, rich ? 0.28 : 0.32), p: [kx, -6.98, -0.72] as const, r: [-2.6, 0, 0] as const,
      })), { rough: 0.5, shadow: false });
      return this.pivot(x, 7.4, 0, limb, hand, nails);
    };
    this.armL = mkArm(-3.0);
    this.armR = mkArm(3.0);
    const mkLeg = (x: number): [THREE.Group, THREE.Group | null] => {
      const inner = x < 0 ? 1 : -1;
      const thigh: Piece = { g: taperVol(1.75, 2.2, 1.9, 0.8, 0.9), p: [0, -1.05, 0] };
      if (!rich) {
        const limb = surf("fur", fur, 1.5, [
          thigh,
          { g: taperVol(1.35, 1.3, 1.4, 0.9, 0.9), p: [0, -2.3, 0.05] },   // shin
        ]);
        const foot = surf("flesh", face, 0.8, [
          { g: vol(1.7, 0.75, 2.3, 0.9), p: [0, -2.95, -0.35] },
          ...[-1, 0, 1].map((i) => ({                                       // toes
            g: vol(0.42, 0.42, 0.7, 1), p: [i * 0.48, -3.0, -1.55] as const,
          })),
        ], { rough: 0.75 });
        return [this.pivot(x, 3.2, 0, limb, foot), null];
      }
      // rich: shin + foot on a KNEE pivot; a five-toed foot with the splayed,
      // opposable big toe an ape actually walks on
      const KY = -1.9, KZ = 0.03;
      const upper = surf("fur", fur, 1.5, [
        thigh,
        { g: new THREE.SphereGeometry(0.66, 12, 8), p: [0, KY, KZ] },      // knee
      ]);
      const shin = surf("fur", fur, 1.5, [
        { g: taperVol(1.35, 1.3, 1.4, 0.9, 0.9), p: [0, -2.3 - KY, 0.05 - KZ] },
      ]);
      const foot = surf("flesh", face, 0.8, [
        { g: vol(1.7, 0.75, 2.3, 0.9), p: [0, -2.95 - KY, -0.35 - KZ] },
        ...[0.05, 0.4, 0.72].map((t) => ({                                 // four small toes (outer side)
          g: blob(0.36, 0.38, 0.62, 1), p: [-inner * t, -3.02 - KY, -1.5 - KZ] as const,
        })),
        { g: blob(0.34, 0.36, 0.55, 1), p: [inner * 0.3, -3.02 - KY, -1.45 - KZ] },
        { g: blob(0.48, 0.45, 0.8, 1), p: [inner * 0.78, -2.98 - KY, -1.1 - KZ], r: [0, inner * 0.5, 0] }, // big toe
      ], { rough: 0.75 });
      const knee = this.pivot(0, KY, KZ, shin, foot);
      return [this.pivot(x, 3.2, 0, upper, knee), knee];
    };
    [this.legL, this.kneeL] = mkLeg(-1.4);
    [this.legR, this.kneeR] = mkLeg(1.4);
    this.torso.add(body, ...faceParts, this.armL, this.armR);
    this.group.add(this.torso, this.legL, this.legR);
  }

  private eye: THREE.Mesh | null = null;

  private buildAlien(): void {
    const skin = "#5ad17a", dark2 = "#1f3a28", glow = "#aaff66";
    const rich = richCreatures();
    this.torso = new THREE.Group();
    // tall spindly body: bulbous head, a RIBBED chitin thorax (segmented plates
    // + a sternal keel + a narrow pelvis), long limbs. The old alien was three
    // boxes and read as a green traffic bollard.
    const bodyPieces: Piece[] = [
      { g: vol(1.9, 1.7, 1.35, 0.9), p: [0, 5.9, 0] },                  // upper thorax
      { g: taperVol(1.6, 1.6, 1.15, 0.72, 0.95), p: [0, 4.5, 0.05] },   // abdomen
      { g: vol(1.55, 0.55, 1.2, 1), p: [0, 3.6, 0.05] },                // pelvis
      { g: vol(0.7, 1.0, 0.7, 1), p: [0, 7.0, -0.05] },                 // neck
      // rib bands wrapping the thorax — the segmentation read
      ...[0, 1, 2].map((i) => ({
        g: vol(2.0 - i * 0.12, 0.24, 1.45 - i * 0.08, 1), p: [0, 5.1 + i * 0.62, 0] as const,
      })),
      { g: taperVol(0.5, 2.4, 0.34, 0.5, 1), p: [0, 5.5, -0.72] },      // sternal keel
    ];
    if (rich) {
      // rich tier: an exoskeletal SPINE — backward-raked dorsal spines down
      // the back plus vertebral knobs — and clavicle/scapula plates, so the
      // profile reads as a built thing rather than a smooth green column
      for (let i = 0; i < 6; i++) {
        bodyPieces.push(
          { g: spike(0.13, 0.55 - i * 0.04), p: [0, 6.6 - i * 0.58, 0.68 - (i > 3 ? 0.08 : 0)], r: [0.95, Math.PI, 0] },
          { g: blob(0.34, 0.26, 0.3, 1), p: [0, 6.5 - i * 0.58, 0.6] },
        );
      }
      for (const sx of [-1, 1]) {
        bodyPieces.push(
          { g: blob(1.0, 0.22, 0.5, 1), p: [sx * 0.62, 6.72, -0.42], r: [0, 0, -sx * 0.22] },  // clavicle
          { g: blob(0.75, 1.0, 0.24, 1), p: [sx * 0.55, 6.0, 0.62], r: [0.1, sx * 0.3, 0] },   // scapula
        );
      }
    }
    const body = surf("chitin", skin, 1.1, bodyPieces, { rough: 0.35, metal: 0.15 });
    const headGeo = new THREE.SphereGeometry(1.6, 20, 14);
    scaleUV(headGeo, 3, 2);
    const headG = new THREE.Mesh(headGeo, new THREE.MeshStandardMaterial(
      monsterMaps("chitin", { color: skin, roughness: 0.35, metalness: 0.15 })));
    headG.scale.set(1, 1.35, 1.1); headG.position.y = 8.0; headG.castShadow = true;
    // cranial crest + cheek ridges — the head was a bare egg
    const crestPieces: Piece[] = [
      { g: taperVol(0.34, 2.4, 1.5, 0.35, 1), p: [0, 9.0, 0.35], r: [0.25, 0, 0] },
      { g: taperVol(0.3, 1.1, 0.7, 0.4, 1), p: [-1.05, 8.1, -0.5], r: [0, 0, 0.5] },
      { g: taperVol(0.3, 1.1, 0.7, 0.4, 1), p: [1.05, 8.1, -0.5], r: [0, 0, -0.5] },
    ];
    if (rich) {
      crestPieces.push(
        // a socket rim around the cyclops eye, and a heavy brow over it
        { g: new THREE.TorusGeometry(0.66, 0.11, 8, 20), p: [0, 8.0, -1.42] },
        { g: blob(1.2, 0.26, 0.5, 1), p: [0, 8.78, -1.3], r: [0.3, 0, 0] },
        // MANDIBLES: two hooked keratin pincers either side of a slit mouth
        { g: spike(0.1, 0.75), p: [-0.36, 6.7, -1.45], r: [Math.PI, Math.PI, 0.3] },
        { g: spike(0.1, 0.75), p: [0.36, 6.7, -1.45], r: [Math.PI, Math.PI, -0.3] },
        { g: blob(0.75, 0.35, 0.45, 1), p: [0, 6.95, -1.45] },                // mouthparts
      );
    }
    const crest = surf("chitin", dark2, 0.7, crestPieces, { rough: 0.3, metal: 0.2 });
    // one big glowing cyclops eye
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.6, 12, 12), new THREE.MeshBasicMaterial({ color: "#ff2fd0" }));
    eye.position.set(0, 8.0, -1.4); this.eye = eye;
    const eyeGlow = new THREE.Mesh(new THREE.SphereGeometry(0.9, 12, 12),
      new THREE.MeshBasicMaterial({ color: "#ff2fd0", transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false }));
    eyeGlow.position.copy(eye.position);
    // glowing chest core, now a lens set into a socket rather than a flat tile
    const core = surf("chitin", dark2, 0.5, [
      { g: vol(0.9, 0.9, 0.3, 1), p: [0, 5.2, -0.72] },
      { g: new THREE.SphereGeometry(0.34, 12, 10), p: [0, 5.2, -0.86], s: [1, 1, 0.6] },
    ], { emissive: glow, ei: 0.9, rough: 0.3, shadow: false });
    const mkArm = (x: number): THREE.Group => {
      const limb = surf("chitin", skin, 0.8, [
        { g: taperVol(0.46, 2.3, 0.46, 0.8, 0.95), p: [0, -1.15, 0] },     // upper
        { g: new THREE.SphereGeometry(0.26, 10, 8), p: [0, -2.35, 0] },    // elbow
        { g: taperVol(0.36, 2.1, 0.36, 0.85, 0.95), p: [0, -3.4, 0] },     // forearm
        { g: taperVol(0.24, 0.9, 0.5, 0.6, 1), p: [-0.05, -2.4, 0.28], r: [0.5, 0, 0] }, // elbow spur
        ...(rich ? [
          { g: new THREE.SphereGeometry(0.32, 10, 8), p: [0, 0, 0] as const, s: [1, 0.85, 1] as const },  // shoulder ball
          { g: blob(0.3, 0.9, 0.24, 1), p: [0, -1.2, -0.2] as const },                                      // flexor ridge
        ] : []),
      ], { rough: 0.35, metal: 0.15 });
      const hand = surf("horn", dark2, 0.4, [
        { g: vol(0.34, 0.3, 0.36, 1), p: [0, -4.6, 0] },
        ...[-1, 0, 1].map((i) => ({                                        // three long digits
          g: spike(0.07, 0.75), p: [i * 0.13, -5.05, -0.1] as const, r: [Math.PI - 0.25, 0, i * 0.18] as const,
        })),
        ...(rich ? [-1, 0, 1].map((i) => ({                                // knuckle joints
          g: new THREE.SphereGeometry(0.075, 6, 5), p: [i * 0.13, -4.78, -0.06] as const,
        })) : []),
      ], { rough: 0.4, shadow: false });
      return this.pivot(x, 6.4, 0, limb, hand);
    };
    this.armL = mkArm(-1.2); this.armR = mkArm(1.2);
    const mkLeg = (x: number): [THREE.Group, THREE.Group | null] => {
      const thigh: Piece = { g: taperVol(0.62, 2.5, 0.62, 0.72, 0.95), p: [0, -1.25, 0] };
      const kneeBall: Piece = { g: new THREE.SphereGeometry(0.3, 10, 8), p: [0, -2.5, 0.05] };
      const footPieces = (oy: number, oz: number): Piece[] => [
        { g: vol(0.5, 0.3, 0.6, 1), p: [0, -4.9 - oy, -0.1 - oz] },
        ...[-1, 1].map((i) => ({ g: spike(0.09, 0.6), p: [i * 0.16, -4.95 - oy, -0.5 - oz] as const, r: [-1.9, 0, 0] as const })),
        { g: spike(0.09, 0.45), p: [0, -4.95 - oy, 0.25 - oz], r: [1.9, 0, 0] },     // dew claw
      ];
      if (!rich) {
        const limb = surf("chitin", skin, 0.9, [
          thigh, kneeBall,
          { g: taperVol(0.44, 2.3, 0.44, 0.85, 0.95), p: [0, -3.65, 0.05] }, // shin
        ], { rough: 0.35, metal: 0.15 });
        const foot = surf("horn", dark2, 0.4, footPieces(0, 0), { rough: 0.4, shadow: false });
        return [this.pivot(x, 3.6, 0, limb, foot), null];
      }
      // rich: shin + foot on a KNEE pivot (with a knee spur) so the stride bends
      const KY = -2.5, KZ = 0.05;
      const upper = surf("chitin", skin, 0.9, [thigh, kneeBall], { rough: 0.35, metal: 0.15 });
      const shin = surf("chitin", skin, 0.9, [
        { g: taperVol(0.44, 2.3, 0.44, 0.85, 0.95), p: [0, -3.65 - KY, 0] },
        { g: taperVol(0.2, 0.7, 0.36, 0.4, 1), p: [0, -0.2, -0.3], r: [-0.5, 0, 0] },  // knee spur
        { g: blob(0.24, 1.3, 0.2, 1), p: [0, -1.1, 0.22] },                            // calf tendon
      ], { rough: 0.35, metal: 0.15 });
      const foot = surf("horn", dark2, 0.4, footPieces(KY, KZ), { rough: 0.4, shadow: false });
      const knee = this.pivot(0, KY, KZ, shin, foot);
      return [this.pivot(x, 3.6, 0, upper, knee), knee];
    };
    [this.legL, this.kneeL] = mkLeg(-0.7);
    [this.legR, this.kneeR] = mkLeg(0.7);
    this.torso.add(body, headG, crest, eye, eyeGlow, core, this.armL, this.armR);
    this.group.add(this.torso, this.legL, this.legR);
  }

  /** Ambient PENIS npc (mega_chibre): a waddling shaft with a pink glans head,
   *  blue veins, and two ball-feet. Fully procedural boxes/spheres like the
   *  others; reuses torso/legL/legR/eye so the shared walk-anim drives it. */
  private buildPenis(): void {
    const shaftC = "#e6a3ab", glansC = "#d15b78", veinC = "#a98fbf", ballC = "#d98890", dark2 = "#7a2530";
    this.torso = new THREE.Group();
    // ANATOMICAL PROFILE (2026-11-03, realism pass): the shaft and the glans
    // are LATHE-TURNED from measured-looking profiles instead of two tapered
    // boxes + a sphere + a torus. The lathe gives a continuous silhouette —
    // root flare, a girth that eases toward the neck, a real corona rim that
    // overhangs the neck, a domed and slightly pointed tip — which no stack of
    // primitives could. Same extents as before (base ~0.7 m, tip 5.32 m, max
    // radius ~1.1 m) so the server hit box still frames it, and one draw call
    // FEWER (the corona is part of the glans now).
    // a z-squash by k maps normals by (nx, ny, nz/k), renormalised. Keeps the
    // lathe's own pole normals (recomputing them yields zero-length ones at r=0)
    const squashNormalsZ = (g: THREE.BufferGeometry, k: number): void => {
      const n = g.attributes.normal;
      for (let i = 0; i < n.count; i++) {
        const x = n.getX(i), y = n.getY(i), z = n.getZ(i) / k;
        const l = Math.hypot(x, y, z) || 1;
        n.setXYZ(i, x / l, y / l, z / l);
      }
      n.needsUpdate = true;
    };
    const lathe = (pts: readonly (readonly [number, number])[], seg: number): THREE.BufferGeometry =>
      new THREE.LatheGeometry(pts.map(([r, y]) => new THREE.Vector2(r, y)), seg);
    const shaftGeo = lathe([
      [0, 0.7], [0.8, 0.72], [0.86, 0.86], [0.82, 1.08], [0.74, 1.4], [0.7, 1.8],
      [0.69, 2.3], [0.7, 2.8], [0.68, 3.3], [0.65, 3.7], [0.62, 3.95], [0, 3.97],
    ], 28);
    // a living shape is never a perfect cylinder: slightly flattened front-to-
    // back, with a gentle upward curve (was the -0.05 rad tilt)
    {
      const pos = shaftGeo.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const y = pos.getY(i), t = Math.max(0, (y - 0.7) / 3.3);
        pos.setZ(i, pos.getZ(i) * 0.92 + 0.12 * t * t);
      }
      pos.needsUpdate = true;
      squashNormalsZ(shaftGeo, 0.92);
    }
    const shaft = surf("flesh", shaftC, 1.1, [{ g: shaftGeo, p: [0, 0, 0] }], { rough: 0.55 });
    const glansGeo = lathe([
      [0.58, 3.6], [0.64, 3.68], [0.8, 3.76], [0.98, 3.84], [1.04, 3.92], [1.0, 4.02],
      [0.97, 4.2], [0.92, 4.45], [0.83, 4.7], [0.68, 4.95], [0.48, 5.16], [0.24, 5.32], [0, 5.38],
    ], 32);
    {
      // the tip leans with the shaft's curve and is a touch narrower front-back
      const pos = glansGeo.attributes.position;
      for (let i = 0; i < pos.count; i++) pos.setZ(i, pos.getZ(i) * 0.95 + 0.12);
      pos.needsUpdate = true;
      squashNormalsZ(glansGeo, 0.95);
    }
    const glans = surf("flesh", glansC, 0.7, [{ g: glansGeo, p: [0, 0, 0] }], { rough: 0.3 }); // moist sheen
    // meatus slit as the "eye" so the turn-toward-player read works
    const slit = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 8), new THREE.MeshBasicMaterial({ color: dark2 }));
    slit.position.set(0, 4.62, -0.72); this.eye = slit;
    // veins: raised, wandering tubes on the skin instead of two square battens
    const veins = surf("flesh", veinC, 0.6, [
      { g: taperVol(0.17, 2.4, 0.15, 0.8, 1), p: [-0.44, 2.6, -0.43], r: [0, 0, 0.12] },
      { g: taperVol(0.15, 2.0, 0.13, 0.8, 1), p: [0.44, 2.4, -0.42], r: [0, 0, -0.2] },
      { g: taperVol(0.13, 1.6, 0.12, 0.8, 1), p: [0.1, 2.9, 0.62], r: [0, 0, 0.28] },
    ], { emissive: veinC, ei: 0.06, rough: 0.5, shadow: false }); // under the skin, not neon
    this.torso.add(shaft, glans, slit, veins);
    const mkBall = (x: number): THREE.Group => {
      const bg = new THREE.SphereGeometry(0.85, 16, 12);
      scaleUV(bg, 2.5, 1.6);
      const b = new THREE.Mesh(bg, new THREE.MeshStandardMaterial(
        monsterMaps("flesh", { color: ballC, roughness: 0.62 })));
      b.scale.set(1, 0.9, 1); b.castShadow = true;
      return this.pivot(x, 1.0, 0, b);
    };
    this.legL = mkBall(-0.7); this.legR = mkBall(0.7);
    // no arms — empty groups keep the shared animation code safe
    this.armL = new THREE.Group(); this.armR = new THREE.Group();
    this.group.add(this.torso, this.legL, this.legR);
  }

  /** mode: 0 walk, 1 fire, 2 climb, 3 roar/chest-beat, 4 descend.
   *  `fresh` is true on frames that consumed a new server patch — it lets the
   *  dead-reckoner tell a genuine link stall (no patch, extrapolate) from a
   *  monster that simply stopped (patches still arriving, velocity → 0). */
  update(x: number, y: number, z: number, yaw: number, mode: number, modeT: number, dt: number, fresh = true): void {
    this.t += dt;
    if (this.warmReg) { unregisterWarmRoot(this.group); this.warmReg = false; } // live now: no longer a warm root
    // smooth follow (server updates arrive at patch rate). The goblin chases
    // much harder: at 6.9 m/s a dt*6 chase trails ~1.1 m — a whole body length
    // behind its (small) server hit box, so shots at the mesh would miss. dt*18
    // keeps the lag ~0.4 m, inside the box's 0.85 m half-width.
    // WHY tight follow instead of the de-jittered delay buffer players/vehicles
    // use (the "fluid" invariant that keeps remotes gliding): the server tests
    // monster hits against the monster's LIVE position with ZERO lag-comp rewind
    // (GunGameRoom fireRound, krakenBox/goblinBox/dragon are built from live
    // state — only PLAYER victims rewind). So the rendered mesh MUST sit on the
    // live pose or shots aimed at it miss. This is a hard hit-registration
    // constraint, not a smoothing preference — do NOT convert monsters to an
    // interpolation delay buffer. The exp-follow low-passes bursty arrivals
    // (C0-continuous → glides, never steps) while staying inside the hit box.
    const follow = this.kind === "goblin" ? 18 : 6;
    // teleports (goblin burrow exit, kraken respawn) must SNAP, not streak a
    // smoothed body across the map
    // 1-exp gain, not min(1, dt*k): the lerp form's chase rate varies with
    // frame rate (~2x residual difference between 30 and 144 fps was the
    // recorded 2026-07-22 audit note); exp keeps the trailing lag ≈ v/k at
    // every refresh rate (goblin at 6.9 m/s: 6.9/18 ≈ 0.38 m, inside the
    // hit box's 0.85 m half-width)
    // Dead-reckon the newest sample when a genuine patch stall leaves the
    // target frozen (see DR_* notes). On a healthy link `age` never reaches
    // DR_GRACE, so `tx/ty/tz` collapse to `x/y/z` and this is the old follow.
    let tx = x, ty = y, tz = z, tyaw = yaw;
    if (!this.drInit) {
      this.drInit = true;
      this.drLiveX = x; this.drLiveY = y; this.drLiveZ = z; this.drLiveT = this.t;
      this.drYaw = yaw;
    } else {
      if (fresh) {
        const moved2 = (x - this.drLiveX) ** 2 + (y - this.drLiveY) ** 2 + (z - this.drLiveZ) ** 2;
        if (moved2 > DR_TELE2) {
          this.drVX = this.drVY = this.drVZ = 0; // burrow / respawn — drop the estimate
          this.drVYaw = 0; // a teleport re-orients too — don't spin toward the old heading
        } else {
          const dtS = this.t - this.drLiveT;
          if (dtS > 1e-3) {
            let vx = (x - this.drLiveX) / dtS, vy = (y - this.drLiveY) / dtS, vz = (z - this.drLiveZ) / dtS;
            const sp = Math.hypot(vx, vy, vz);
            if (sp > DR_MAXSPD) { const k = DR_MAXSPD / sp; vx *= k; vy *= k; vz *= k; }
            // EMA blend: softens a coalesced-patch short interval AND lets a
            // stop (identical samples → v measured as 0) decay the estimate.
            this.drVX += (vx - this.drVX) * 0.5;
            this.drVY += (vy - this.drVY) * 0.5;
            this.drVZ += (vz - this.drVZ) * 0.5;
            // angular velocity on the shortest arc (rad/s), same EMA + clamp; a
            // stop leaves identical yaw samples → measured 0 → decays the spin.
            let w = Math.atan2(Math.sin(yaw - this.drYaw), Math.cos(yaw - this.drYaw)) / dtS;
            if (w > DR_YAW_MAXSPD) w = DR_YAW_MAXSPD; else if (w < -DR_YAW_MAXSPD) w = -DR_YAW_MAXSPD;
            this.drVYaw += (w - this.drVYaw) * 0.5;
          }
        }
        this.drLiveX = x; this.drLiveY = y; this.drLiveZ = z; this.drLiveT = this.t;
        this.drYaw = yaw;
      }
      const lead = Math.min(DR_LEAD, Math.max(0, (this.t - this.drLiveT) - DR_GRACE));
      if (lead > 0) {
        let ex = this.drVX * lead, ey = this.drVY * lead, ez = this.drVZ * lead;
        const em = Math.hypot(ex, ey, ez);
        if (em > DR_MAXLEAD) { const k = DR_MAXLEAD / em; ex *= k; ey *= k; ez *= k; }
        tx = x + ex; ty = y + ey; tz = z + ez;
        let ew = this.drVYaw * lead;
        if (ew > DR_YAW_MAXLEAD) ew = DR_YAW_MAXLEAD; else if (ew < -DR_YAW_MAXLEAD) ew = -DR_YAW_MAXLEAD;
        tyaw = yaw + ew;
      }
    }
    if (this.group.position.distanceToSquared(SCRATCH_POS.set(tx, ty, tz)) > DR_TELE2) this.group.position.copy(SCRATCH_POS);
    else this.group.position.lerp(SCRATCH_POS, 1 - Math.exp(-dt * follow));
    const dy = tyaw - this.group.rotation.y;
    this.group.rotation.y += Math.atan2(Math.sin(dy), Math.cos(dy)) * (1 - Math.exp(-dt * follow));

    if (this.creatureRig) { this.updateCreature(this.creatureRig, mode, modeT, dt); return; }
    const swing = Math.sin(this.t * (this.kind === "godzilla" ? 2.2 : 3.2));
    if (this.kind === "godzilla") {
      const walking = mode === 0;
      this.legL.rotation.x = walking ? swing * 0.45 : 0;
      this.legR.rotation.x = walking ? -swing * 0.45 : 0;
      this.armL.rotation.x = walking ? -swing * 0.3 : -0.6;
      this.armR.rotation.x = walking ? swing * 0.3 : -0.6;
      // KNEES (rich tier): flex through each leg's forward swing — the swing
      // phase is where d(leg)/dt > 0, i.e. cos > 0 for the left leg — so the
      // foot clears the ground instead of the whole leg pivoting like a stilt
      const stride = Math.cos(this.t * 2.2);
      if (this.kneeL) this.kneeL.rotation.x = walking ? -Math.max(0, stride) * 0.55 - 0.05 : 0;
      if (this.kneeR) this.kneeR.rotation.x = walking ? -Math.max(0, -stride) * 0.55 - 0.05 : 0;
      for (let i = 0; i < this.tail.length; i++) {
        this.tail[i].rotation.y = Math.sin(this.t * 1.6 + i * 0.9) * 0.28;
        // a slower vertical undulation layered on the sway: a heavy tail rides
        // up and down with the hips instead of swinging on a flat plane
        this.tail[i].rotation.x = Math.sin(this.t * 1.1 + i * 0.7) * 0.045;
      }
      // weight transfer: the body drops at full stride and rides highest over
      // the planted leg, and rolls toward it; idle = rear back and BREATHE
      this.torso.rotation.x = walking ? Math.sin(this.t * 4.4) * 0.02 : -0.12 + Math.sin(this.t * 1.3) * 0.015;
      this.torso.rotation.z = walking ? swing * 0.025 : 0;
      this.torso.position.y = walking ? (1 - Math.abs(swing)) * 0.14 : 0;
      const firing = mode === 1 && modeT > 0.6;
      // JAW: the chin DROPS to open (negative x — the model faces -z). The old
      // line drove it to +0.6 rad in every non-fire mode, which rammed the jaw
      // up inside the snout, so the "opens as the tell" never showed; now it
      // eases shut at rest and gapes during the breath windup.
      if (this.jaw) {
        const jawT = mode === 1 ? -Math.min(0.65, modeT * 1.4) : 0;
        this.jaw.rotation.x += (jawT - this.jaw.rotation.x) * (1 - Math.exp(-dt * 10));
      }
      // breath cone STARTING AT the mouth. The model faces -z, so forward is
      // (-sin(yaw), .., -cos(yaw)) — the old +sin/+cos fired out the BACK of
      // the head, making the flame look detached from the mouth.
      const mouth = SCRATCH_MOUTH.set(0, 10.2, -3.0).applyAxisAngle(UP_AXIS, this.group.rotation.y).add(this.group.position);
      const dir = SCRATCH_DIR.set(-Math.sin(this.group.rotation.y), -0.1, -Math.cos(this.group.rotation.y));
      this.breath.forEach((f, i) => {
        f.visible = firing;
        if (!firing) return;
        // hug the mouth: first ball right at the lips, growing outward
        const d = 0.4 + i * 2.0 + Math.sin(this.t * 17 + i * 2.4) * 0.35;
        f.position.copy(mouth).addScaledVector(dir, d);
        f.position.y = Math.max(0.8, f.position.y);
        const s = 0.55 + i * 0.28 + Math.sin(this.t * 23 + i) * 0.12;
        f.scale.setScalar(s);
        (f.material as THREE.MeshBasicMaterial).opacity = 0.9 - i * 0.1;
      });
      if (this.breathGlow) {
        this.breathGlow.visible = mode === 1;
        if (mode === 1) {
          this.breathGlow.position.copy(mouth);
          this.breathGlow.scale.setScalar(0.8 + Math.sin(this.t * 20) * 0.25 + (firing ? 0.7 : modeT));
        }
      }
    } else if (this.kind === "penis") {
      // waddle: the two ball-feet swing opposite, the shaft sways, gentle bob
      this.legL.rotation.x = swing * 0.5;
      this.legR.rotation.x = -swing * 0.5;
      this.torso.rotation.z = Math.sin(this.t * 2.2) * 0.08;
      this.torso.position.y = Math.abs(Math.sin(this.t * 4)) * 0.15;
      if (this.eye) this.eye.scale.setScalar(1 + Math.sin(this.t * 3) * 0.12);
    } else if (this.kind === "alien") {
      // creepy glide-walk: slight sway, long arms swinging, eye pulses;
      // brightens + flares when firing (mode 1)
      this.legL.rotation.x = swing * 0.5;
      this.legR.rotation.x = -swing * 0.5;
      // knees (rich tier) fold through the forward swing, like godzilla's
      const strideA = Math.cos(this.t * 3.2);
      if (this.kneeL) this.kneeL.rotation.x = -Math.max(0, strideA) * 0.7;
      if (this.kneeR) this.kneeR.rotation.x = -Math.max(0, -strideA) * 0.7;
      this.armL.rotation.x = -swing * 0.4; this.armR.rotation.x = swing * 0.4;
      this.torso.rotation.z = Math.sin(this.t * 1.7) * 0.06;
      if (this.eye) {
        const firing = mode === 1;
        const s2 = firing ? 1.6 : 1 + Math.sin(this.t * 3) * 0.15;
        this.eye.scale.setScalar(s2);
        (this.eye.material as THREE.MeshBasicMaterial).color.set(firing ? "#ffffff" : "#ff2fd0");
      }
    } else if (this.kind === "kraken") {
      // rest = writhing splay; WINDUP (mode 1) rears the tentacles up high;
      // SLAM (mode 2) whips them down-and-out with a punchy ease; mode 9 = SLAIN
      // → tentacles go limp and droop as the body sinks.
      const dying = mode === 9;
      const rear = mode === 1 ? Math.min(1, modeT / 1.1) : 0;
      const slam = mode === 2 ? Math.max(0, 1 - modeT / 0.42) : 0;
      const slamK = slam * slam; // sharper impact
      if (this.krakenRig) updateKrakenModel(this.krakenRig, this.t, dt, rear, slamK, dying);
      for (let i = 0; i < this.tentacles.length; i++) {
        const tc = this.tentacles[i];
        const w = Math.sin(this.t * 1.4 + tc.phase);
        if (dying) {
          // 1-exp gain, not a fixed *0.1 per frame: the death droop must settle
          // at the same wall-clock rate on every refresh (parity with the body
          // follow at 314/316 and the goblin keel-over). 0.1/frame ≈ 6.3/s @60.
          const dg = 1 - Math.exp(-dt * 6.3);
          tc.base.rotation.x += (0.5 - tc.base.rotation.x) * dg;
          tc.mid.rotation.x += (0.75 - tc.mid.rotation.x) * dg;
          tc.tip.rotation.x += (0.95 - tc.tip.rotation.x) * dg;
          tc.base.rotation.z = w * 0.03;
          continue;
        }
        tc.base.rotation.x = -0.55 - rear * 1.0 + slamK * 1.7 + w * 0.12;
        tc.mid.rotation.x = 0.5 + w * 0.35 - rear * 0.55 + slamK * 0.8;
        tc.tip.rotation.x = 0.5 + w * 0.5 + slamK * 0.6;
        tc.base.rotation.z = w * 0.1;
      }
      this.group.rotation.z = Math.sin(this.t * 0.7) * 0.03 + (dying ? 0 : slamK * 0.06);
    } else if (this.kind === "goblin") {
      if (mode === 9) {
        // POPPED / burrowed out: keel over and shrink away (removed ≤1.4 s later)
        this.torso.rotation.x += (-1.4 - this.torso.rotation.x) * (1 - Math.exp(-dt * 6)); // frame-rate-independent ease
        const s = Math.max(0.05, this.group.scale.x - dt * 0.5);
        this.group.scale.setScalar(s);
      } else if (mode === 2) {
        // DIGGING: frantic spin-drill into the ground (~0.7 s, then it
        // teleports and the snap above puts it at the exit)
        this.torso.rotation.y += dt * 22;
        this.torso.position.y = Math.max(-1.1, this.torso.position.y - dt * 1.6);
        this.legL.rotation.x = this.legR.rotation.x = 0.6;
      } else {
        this.torso.rotation.y = 0;
        // frantic scamper — twice the kaiju cadence, plus a hop bounce so the
        // silhouette reads "fleeing critter" from across the map
        const scamper = Math.sin(this.t * 13);
        this.legL.rotation.x = scamper * 0.95;
        this.legR.rotation.x = -scamper * 0.95;
        this.armL.rotation.x = -scamper * 0.8;
        this.armR.rotation.x = scamper * 0.8;
        this.torso.position.y = Math.abs(scamper) * 0.09;
        this.torso.rotation.x = 0.16; // leaning into the sprint
        if (this.sack) this.sack.rotation.z = Math.sin(this.t * 6.5) * 0.14; // the hoard sloshes
      }
    } else {
      // KONG
      // knees (rich tier) relax to straight outside the walk/climb poses below
      let kneeLT = 0, kneeRT = 0;
      if (mode === 0) {
        // knuckle-walk: heavy alternating arm swings
        this.armL.rotation.x = -0.5 + swing * 0.5;
        this.armR.rotation.x = -0.5 - swing * 0.5;
        this.legL.rotation.x = -swing * 0.35;
        this.legR.rotation.x = swing * 0.35;
        // the left leg swings forward while -cos > 0; fold that knee
        const strideK = Math.cos(this.t * 3.2);
        kneeLT = -Math.max(0, -strideK) * 0.6 - 0.08;
        kneeRT = -Math.max(0, strideK) * 0.6 - 0.08;
        this.torso.rotation.x = 0.35; // hunched forward
        this.torso.rotation.z = swing * 0.05;
      } else if (mode === 2 || mode === 4) {
        // climbing / sliding: alternating reach-up
        const c = Math.sin(this.t * (mode === 2 ? 5 : 8));
        this.armL.rotation.x = -2.4 + c * 0.5;
        this.armR.rotation.x = -2.4 - c * 0.5;
        this.legL.rotation.x = -0.8 + c * 0.4;
        this.legR.rotation.x = -0.8 - c * 0.4;
        kneeLT = -0.5 - c * 0.3; kneeRT = -0.5 + c * 0.3; // feet braced on the wall
        this.torso.rotation.x = -0.15; // pressed to the wall
      } else if (mode === 3) {
        // CHEST BEAT: lean back and hammer alternately
        const beat = Math.sin(this.t * 11);
        this.torso.rotation.x = -0.35;
        this.armL.rotation.x = beat > 0 ? -2.2 : -1.2;
        this.armR.rotation.x = beat > 0 ? -1.2 : -2.2;
        this.armL.rotation.z = 0.5;
        this.armR.rotation.z = -0.5;
        this.legL.rotation.x = 0;
        this.legR.rotation.x = 0;
      }
      if (mode !== 3) { this.armL.rotation.z = 0.15; this.armR.rotation.z = -0.15; }
      if (this.kneeL) this.kneeL.rotation.x = kneeLT;
      if (this.kneeR) this.kneeR.rotation.x = kneeRT;
      // the ROAR (rich tier): the jaw drops wide through the chest beat and
      // shudders with it; otherwise it hangs just parted, breathing
      if (this.kongJaw) {
        const jawT = mode === 3 ? -0.5 + Math.sin(this.t * 22) * 0.04 : -0.04 - Math.max(0, Math.sin(this.t * 1.4)) * 0.05;
        this.kongJaw.rotation.x += (jawT - this.kongJaw.rotation.x) * (1 - Math.exp(-dt * 12));
      }
    }
  }

  /** Rigged-model animation: clip from {mode, modeT}, walk rate from the
   *  dead-reckoned ground speed (stride-matched, scale-aware), plus the
   *  goblin's burrow spin and pop-shrink that the procedural one also does. */
  private updateCreature(r: CreatureRig, mode: number, modeT: number, dt: number): void {
    const speed = Math.hypot(this.drVX, this.drVZ) / (this.group.scale.x || 1);
    updateCreatureModel(r, this.t, dt, mode, modeT, speed);
    if (this.kind !== "goblin") return;
    if (mode === 9) {
      // POPPED / burrowed out: the death clip keels it over; shrink it away
      this.group.scale.setScalar(Math.max(0.05, this.group.scale.x - dt * 0.5));
    } else if (mode === 2) {
      // DIGGING: spin-drill into the ground (then it teleports and snaps)
      r.root.rotation.y += dt * 22;
      r.root.position.y = Math.max(-1.1, r.root.position.y - dt * 1.6);
    } else if (r.root.rotation.y !== 0 || r.root.position.y !== 0) {
      r.root.rotation.y = 0; r.root.position.y = 0;
    }
  }

  dispose(scene: THREE.Scene): void {
    if (this.warmReg) { unregisterWarmRoot(this.group); this.warmReg = false; }
    if (this.creatureRig) { this.creatureRig.mixer.stopAllAction(); }
    this.disposed = true;
    scene.remove(this.group);
    // monsters despawn/respawn mid-match — ~30 geometries+materials each
    disposeTree(this.group);
    if (this.krakenRig) { this.krakenRig.mixer.stopAllAction(); this.krakenRig = null; }
    this.creatureRig = null;
  }
}

/** Free a monster subtree's own buffers/materials. Anything tagged
 *  userData.shared (module-cached textures, the cached glTF kraken's
 *  geometry/material/textures) is left alone; a skinned clone's per-instance
 *  Skeleton (bone texture) is freed. */
function disposeTree(root: THREE.Object3D): void {
  disposeSkinned(root);
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!(m.geometry as { userData?: { shared?: boolean } })?.userData?.shared) m.geometry?.dispose?.();
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      if (mat.userData?.shared) continue;
      const t = mat as THREE.MeshStandardMaterial;
      if (t.map && !t.map.userData?.shared) t.map.dispose();
      mat.dispose();
    }
  });
}
